import { aigcFormStore } from '@/components/aigc-form/store';
import { sendBroadcast } from '@/hooks/useBroadcast';
import membershipStore from '@/components/membership/store';
import { HelperCore } from "@/common/helper";
import userStore from '@/store/user';
import TrailExaustedModal from "@/components/modal/trial-exhausted";
import { request } from '@src/common';
import { message } from 'antd';
import MarketingStore from '@/store/marketing';
import { makeAutoObservable, runInAction } from 'mobx';
import stateStore from '../square-video/state-store';

export const COVER_MAP: Record<string, CoverInfo> = {
  'image_video': {
    title: '图生视频',
    icon: 'https://p5.ssl.qhimg.com/t110b9a9301231ae0c611484445.png',
    cover: 'https://p5.ssl.qhimg.com/t110b9a930128d52b9ce9b9c29e.png',
    url: 'https://s0.ssl.qhres2.com/static/e9d62ade1f0c9c42.webm',
    describe: '图片变视频，让你的图片焕发新生'
  },
  'text_video': {
    title: '文生视频',
    icon: 'https://p2.ssl.qhimg.com/t110b9a9301cdf1fee116d275e5.png',
    cover: 'https://p4.ssl.qhimg.com/t110b9a93017ea0c1df36dbb29d.png',
    url: 'https://s5.ssl.qhres2.com/static/b1fb7a130afab49e.webm',
    describe: '将文字转化为视频，让你的创意轻松呈现'
  },
};

export interface CoverInfo {
  title: string;
  icon: string;
  url: string;
  describe: string;
  cover?: string;
}

export interface GenerateVideoOutputProps {
  activeType: string;
}

export const VIDEO_SIDEBAR_MENU = [
  {
    key: 'effect_video',
    icon: 'work-magic',
    label: '热门特效',
    path: '/square/video?page_type=effect',
    // 添加额外路径配置
    extraPaths: ['/generate/video?form_type=effect_video'],
  },
  {
    key: 'video',
    icon: 'work-img2img',
    label: '图生视频',
    children: [
      {
        key: 'image_video',
        label: '单图模式',
        path: '/generate/video?form_type=image_video',
      },
      {
        key: 'image_video_part',
        label: '首尾帧',
        path: '/generate/video?form_type=image_video_part',
      },
      {
        key: 'image_video_reference',
        label: '多图参考',
        path: '/generate/video?form_type=image_video_reference',
      },
      {
        key: 'image_video_frame',
        label: '视频帧',
        path: '/generate/video?form_type=image_video_frame',
      },
    ],
  },
  {
    key: 'text_video',
    icon: 'work-text2video',
    label: '文生视频',
    path: '/generate/video?form_type=text_video',
  },
];

const sortType = {
  text_video: 1,
  image_video: 2,
  image_video_frame: 3,
  image_video_part: 4,
  image_video_reference: 5,
  effect_video: 7,
}

export const MODEL_TYPE = {
  text_video: '',
  image_video: 'single',
  image_video_frame: 'frame',
  image_video_part: 'part',
  image_video_reference: 'reference',
}

class Store {
  constructor() {
    makeAutoObservable(this);
  }
  
  taskType: string = 'image_video';
  taskList: any[] = [];
  activeTaskId: string | null = '';
  videoResult: any = null;
  page: number | string = 0;
  pageSize: number = 20;
  hasMore: boolean = true;
  taskLoading: boolean = false;
  // 存储轮询处理器的引用，用于取消轮询
  pollingTimer: number | null = null;
  // 存储正在轮询的任务ID集合
  pollingTaskIds: Set<string> = new Set();
  // 上次提示的任务ID，用于避免重复提示
  lastNotifiedTaskId: string | null = null;
  coverInfo: unknown = {}
  taskListVisible: boolean = true;
  // 用于提交任务等待时显示loading
  showLoading: boolean = false;
  // 剩余次数
  remaining: number = 0;
  // 历史活动任务
  historyActiveTask: any = {};
  // 历史表单
  historyFormParam: any = {};
  source: string = '';
  memberBenefits = {
    expresswayUsable: '', // 高速通道 member 会员可以 all 所有用户可用
  }

  setMemberBenefits = (val) => {
    this.memberBenefits = val;
  };
  
  setSource = (val) => {
    this.source = val;
  }

  setShowLoading = (val) => {
    this.showLoading = val;
  }

  setHistoryFormParam = (formType, val) => {
    this.historyFormParam[formType] = val;
  }

  setHistoryActiveTask = (formType, val) => {
    this.historyActiveTask[formType] = val;
  }

  setTaskListVisible = () => {
    this.taskListVisible = !this.taskListVisible;
  }
  setVideoResult = () => {
    this.videoResult = null;
    this.activeTaskId = null;
  }
  // 设置封面信息
  setCoverInfo = (info) => {
    this.coverInfo = info;
  }

  // 设置活动任务
  setActiveTask = (item?, formType?) => {
    if (!item) {
      this.activeTaskId = null;
      this.videoResult = null;
      return;
    }
    runInAction(() => {
      this.activeTaskId = item.id_str;
      this.historyActiveTask[formType] = item.id_str;
      // 解析并设置视频结果
      if (item.data) {
        try {
          this.videoResult = JSON.parse(item.data);
        } catch (e) {
          console.error('解析任务数据失败:', e);
          this.videoResult = null;
        }
      } else {
        this.videoResult = null;
      }
    });
  }
  
  // 基于activeTaskId更新videoResult
  updateVideoResultFromActiveTask = () => {
    if (!this.activeTaskId) {
      this.videoResult = null;
      return;
    }
    const activeTask = this.taskList.find(task => task.id_str === this.activeTaskId);
    if (activeTask && activeTask.data) {
      try {
        this.videoResult = JSON.parse(activeTask.data);
      } catch (e) {
        console.error('解析任务数据失败:', e);
        this.videoResult = null;
      }
    } else {
      this.videoResult = null;
    }
  }

  // 获取当前选中的任务
  get activeTask() {
    return this.taskList.find(item => item.id_str === this.activeTaskId) || null;
  }
  
  cancelTask = (item) => {
    request.post('/task/cancel?appsource=so&device_platform=Web', {id_str: item.id_str}, {
      // @ts-ignore
      dataIsJson: true,
    }).then(res => {
      if (res.data.result === 'finish') {
        // 更新纳米币
        userStore.getCoinBalance();
        this.pollingTaskIds.delete(item.id_str);
        // 更新剩余次数
        this.refreshRemainingCounts();
        const taskIndex = this.taskList?.findIndex(task => task.id_str === item.id_str);
        if (taskIndex !== -1) {
          runInAction(() => {
            const list =  this.taskList?.filter(task => task.biz_type === this.taskType);
            // 从列表中删除任务
            this.taskList = [
              ...list.slice(0, taskIndex),
              ...list.slice(taskIndex + 1)
            ];
            this.videoResult = null;
            this.updateVideoResultFromActiveTask();
          });
        }
      } else {
        message.error(res.data.msg || '取消任务失败');
      }
    }).catch(error => {
      message.error('取消任务失败');
    });
  }

  // 重试任务
  onRetry = (item) => {
     // 更新任务状态为进行中
     this.updateTaskStatus(item.id_str, 1);
    const mid = JSON.parse(item.data)?.mid;
    let param = {  }
    let url = ''
    if (MarketingStore.isMarketingCloud) {
      url = '/video/submit?appsource=so&device_platform=Web';
      param = { video_id: mid, method: 'retry', biz_key: '360mkt'};
    } else {
      url = '/video/retry/v2?appsource=so&device_platform=Web';
      param = { biz_id: item.biz_id };
    }
    
    request.post(url, param, {
      // @ts-ignore
      dataIsJson: true,
    }).then(res => {
       // 将任务添加到轮询列表
       this.pollingTaskIds.add(item.id_str);
       // 更新纳米币
       userStore.getCoinBalance();
       // 如果当前没有轮询在进行，启动轮询
       if (this.pollingTimer === null) {
         this.pollTasksStatus();
       } else {
         // 如果已有轮询在进行，确保此任务被包含在轮询中
         this.startPollingTasks();
       }
    }).catch(err => {
      if ([301039, 316000].includes(err.code)) {
         if (HelperCore.isMac()) {
            TrailExaustedModal.show({
              isMember: err.code === 301039,
              nanoCostLack: err.code === 316000
            })
          } else {
            membershipStore.setData({
              show: true,
              checkstandType:
                err.code === 301039 ? 'openMembership' : 'namiRecharge',
              feature: err.data.feature_id,
              position: 'aibot_video_creation',
            });
            if (err.code === 316000) {
              message.error('纳米余额不足，请充值');
            }
          }
        } else {
          message.error(err.msg || '重试失败，请稍后再试');
        }
       // 任务状态还原
       this.updateTaskStatus(item.id_str, 3);
      
    });
  }

  // 删除任务项并自动选择下一个
  deleteTaskItem = async (item, index) => {
    // 从轮询列表中移除任务
    this.pollingTaskIds.delete(item.id_str);
    request.post('/task/delete?appsource=so&device_platform=Web', {id_str: item.id_str}, {
      // @ts-ignore
      dataIsJson: true,
    }).then(res => {
      if (res.data.result === 'finish') {
        runInAction(() => {
          if (index !== -1) {
            const imageVideoTypes = ['image_video', 'image_video_part', 'image_video_reference', 'image_video_frame'];
            const list =  this.taskList.filter(task => imageVideoTypes.includes(task.biz_type) && imageVideoTypes.includes(this.taskType) || task.biz_type === this.taskType);
            // 创建新数组而不是修改原数组
            this.taskList = [
              ...list.slice(0, index),
              ...list.slice(index + 1)
            ];
            
            // 更新活动项
            this.activeTaskId = null;
            
            // 根据新的activeTaskId更新videoResult
            this.updateVideoResultFromActiveTask();
            sendBroadcast('work-task-changed');
            stateStore.setTaskUpdate(true);
          }
        });
        
        message.success('删除成功');
      }
    }).catch(error => {
      console.error('删除任务失败:', error);
      message.error('删除失败，请稍后再试');
    });
  }

  // 初始化任务列表
  initTaskList = async (type = 'image_video') => {
    // 重置状态
    this.page = 0;
    this.hasMore = true;
    // this.taskList = [];
    this.taskType = type;
    // this.videoResult = null;
    // this.activeTaskId = null;
    
    // 取消现有轮询
    this.stopPollingTasks();
    // 加载第一页数据
    await this.loadMoreTasks('init');
    this.refreshRemainingCounts();
    // 如果有进行中的任务，启动轮询
    if (this.pollingTaskIds.size > 0) {
      this.startPollingTasks();
    }
    // 初始化完成后，如果第一个是进行中的任务 则选择中第一个
    runInAction(() => {
      // if (this.taskList[0]?.status === 1 && this.taskList[0]?.biz_type === type) {
        // this.setActiveTask(this.taskList[0]);
      // }
    });
  }
  
  // 加载更多数据
  loadMoreTasks = async (type = '') => {
    if (!this.hasMore || this.taskLoading) return;
    
    this.taskLoading = true;
    
    try {
      const nextPage = type === 'init' ? '' : +this.page + 1;
      const params = {
        status_list: [1, 2, 3],
        biz_type_list: this.taskType === 'text_video' ? ['text_video'] : ['image_video'],
        npt: `${nextPage}`,
        page_size: this.pageSize,
        sort_type_list: sortType[this.taskType] ? [sortType[this.taskType]] : []
      }
      console.log('taskType', this.taskType);
      // @ts-ignore
      const response = await request.post('/task/list?appsource=so&device_platform=Web', params, { dataIsJson: true});
      const newTasks = response.data.list || [];
       // 处理任务列表中的biz_type
      const processedTasks = newTasks?.map(task => {
        let taskData;
        try {
          taskData = task.data ? JSON.parse(task.data) : null;
        } catch (e) {
          console.error('解析任务数据失败:', e);
          taskData = null;
        }
        // 如果template_id有值,将biz_type改为effect_video
        if (taskData && taskData?.template_id) {
          return {
            ...task,
            biz_type: 'effect_video'
          };
        } else {
          // 首尾帧
          if (taskData?.mode === 'part') {
            return {
             ...task,
              biz_type: 'image_video_part'
            }; 
          }
          // 多图
          if (taskData?.mode === 'reference') {
            return {
            ...task,
              biz_type: 'image_video_reference'
            }; 
          }
          // 视频帧
          if (taskData?.mode === 'frame') {
            return {
               ...task,
              biz_type: 'image_video_frame'
            };
          }
          return task;
        }
        
      });

      runInAction(() => {
        if (processedTasks.length === 0) {
          this.hasMore = newTasks.length !== 0;
          if (type === 'init') {
            this.taskList = []
          }
        } else {
          this.taskList = type === 'init' ? processedTasks : [...this.taskList, ...processedTasks];
          this.page = type === 'init' ? 0 : nextPage;
          const taskId = this.historyActiveTask[this.taskType] || '';
          const taskItem = this.taskList.find((item) => item.id_str === taskId);
          if (this.source !== 'work') {
            if (this.source === 'image') {
              this.setHistoryActiveTask(this.taskType, null)
              this.setActiveTask(undefined, this.taskType);
            } else if (taskItem) {
              this.setActiveTask(taskItem, this.taskType);
            } else {
              this.setActiveTask(undefined, this.taskType);
            }
          } else {
            this.setActiveTask(undefined, this.taskType);
            this.setHistoryActiveTask(this.taskType, null)
          }
          // 将所有进行中的任务添加到轮询列表
          newTasks.forEach(task => {
            if (task.status === 1) {
              this.pollingTaskIds.add(task.id_str);
            }
          });
        }
        this.taskLoading = false;
      });
    
    } catch (error) {
      console.error('加载更多任务失败', error);
      runInAction(() => {
        this.taskLoading = false;
      });
    }
  }
  refreshRemainingCounts = async () => {
    // 非营销云不用更新次数
    if (!MarketingStore.isMarketingCloud) {
      return
    }
    // 调用 aigcFormStore 刷新剩余次数
    try {
      this.remaining = undefined;
     const remaining = await aigcFormStore.fetchRemainingCounter('video')
      runInAction(() => {
        this.remaining = remaining
      });
    } catch (error) {
      console.error('获取剩余次数失败:', error);
    }
   
  }
  // 开始轮询进行中的任务
  startPollingTasks = () => {
    
    // 先停止之前的轮询
    this.stopPollingTasks();
    
    // 检查是否有进行中任务需要轮询
    if (this.pollingTaskIds.size === 0) {
      return;
    }
    
    // 立即开始轮询
    this.pollTasksStatus();
  }
  
  // 停止轮询
  stopPollingTasks = () => {
    if (this.pollingTimer !== null) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
  }
  
  // 轮询任务状态
  pollTasksStatus = async () => {
    if (this.pollingTaskIds.size === 0) {
      this.stopPollingTasks();
      return;
    }
    
    try {
      // 构造批量查询参数
      const taskIds = Array.from(this.pollingTaskIds);
      
      // 调用批量查询API
      const response = await request.post('/task/list?appsource=so&device_platform=Web',
        { 
          id_list_str: taskIds, 
          status_list: [1, 2, 3],  
          biz_type_list: this.taskType === 'text_video' ? ['text_video'] : ['image_video'],
        },
        {
          // @ts-ignore
          dataIsJson: true 
        }
      );
      if (response?.data && response?.data?.list) {
        const taskDetails = response.data.list || [];
        // 用于判断是否有任务状态发生了变化
        let statusChanged = false;
        // 处理返回的任务列表
        taskDetails.forEach(taskDetail => {
          const taskId = taskDetail.id_str;
          const taskStatus = taskDetail.status;
          // 如果任务已完成或失败，更新状态并移除轮询
          if (taskStatus === 2 || taskStatus === 3) {
            statusChanged = true;
            this.updateTaskStatus(taskId, taskStatus, taskDetail);
            this.pollingTaskIds.delete(taskId);
          } else if(taskStatus === 1) {
            // 如果任务仍在进行中，只更新状态
            this.updateTaskStatus(taskId, taskStatus, taskDetail);
          }
        });
        if (statusChanged) {
          this.refreshRemainingCounts();
        }
        // 如果还有任务需要轮询，设置下一次轮询
        if (this.pollingTaskIds.size > 0) {
          this.pollingTimer = window.setTimeout(() => {
            this.pollTasksStatus();
          }, 3000); // 每3秒轮询一次
        } else {
          this.pollingTimer = null;
        }
      } else {
      
        // 即使出错也继续轮询
        this.pollingTimer = window.setTimeout(() => {
          this.pollTasksStatus();
        }, 5000); // 出错后延长轮询间隔
      }
    } catch (error) {
      console.error('轮询任务状态出错:', error);
      
      // 出错后也继续轮询
      this.pollingTimer = window.setTimeout(() => {
        this.pollTasksStatus();
      }, 5000);
    }
  }
  
  // 更新任务状态
  updateTaskStatus = (taskId, status, taskData = null) => {
    if (!taskId) return;
    if(status !== 1 && this.lastNotifiedTaskId !== taskId) {
      // 更新纳米币
      userStore.getCoinBalance();
      status === 2 ?  message.success('任务完成'):  message.error('任务失败');
      // 更新上次提示的任务ID
      this.lastNotifiedTaskId = taskId;
    }
    runInAction(() => {
      const taskIndex = this.taskList.findIndex(task => task.id_str === taskId);
      
      if (taskIndex !== -1) {
        let updatedTaskData = taskData;
        if (taskData?.data) {
          try {
            const parsedData = JSON.parse(taskData.data);
            if (parsedData?.template_id) {
              updatedTaskData = {
                ...taskData,
                biz_type: 'effect_video'
              };
            } else {
              // 首尾帧
              if (parsedData?.mode === 'part') {
                updatedTaskData = {
                 ...taskData,
                  biz_type: 'image_video_part'
                }; 
              }
              // 多图
              if (parsedData?.mode === 'reference') {
                updatedTaskData = {
                ...taskData,
                  biz_type: 'image_video_reference'
                }; 
              }
              // 视频帧
              if (parsedData?.mode === 'frame') {
                updatedTaskData = {
                   ...taskData,
                  biz_type: 'image_video_frame'
                } 
              }
            }
          } catch (e) {
            console.error('解析任务数据失败:', e);
          }
        }
        
        // 创建更新后的任务对象
        const updatedTask = {
          ...this.taskList[taskIndex],
          status,
          ...(updatedTaskData ? updatedTaskData : {}),
        };
        
        // 更新任务列表
        this.taskList = [
          ...this.taskList.slice(0, taskIndex),
          updatedTask,
          ...this.taskList.slice(taskIndex + 1)
        ];
        
        // 如果更新的是当前活动任务，也更新视频结果
        if (this.activeTaskId === taskId && taskData?.data) {
          try {
            this.videoResult = JSON.parse(taskData.data);
          } catch (e) {
            console.error('解析任务数据失败:', e);
          }
        }
      }
    });
  }
  
  // 添加新任务
  addNewTask = (data) => {
    if (!data) return;
    const taskData = Array.isArray(data)? data : [data];
    runInAction(() => {
      // 将新任务添加到列表开头
      this.taskList = [...taskData, ...this.taskList];
      this.activeTaskId = taskData[0].id_str;
      const videoResult = taskData[0].data
      // 如果有数据，更新视频结果
      if (videoResult) {
        try {
          this.videoResult = JSON.parse(videoResult);
        } catch (e) {
          console.error('解析新任务数据失败:', e);
          this.videoResult = null;
        }
      } else {
        this.videoResult = null;
      }
      sendBroadcast('work-task-changed');
      stateStore.setTaskUpdate(true);
    });
    taskData.forEach((item) => {
      if (item.status === 1) {
        this.pollingTaskIds.add(item.id_str);
      }
    });
    this.startPollingTasks();
  }
}

export const GenerateVideoStore = new Store();